import { invitationKeys, type LedgerMembership } from '@taler/api-client'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'

export interface UseAcceptInvitation {
  readonly isAccepting: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly membership: ComputedRef<LedgerMembership | undefined>
  accept(token: string): void
}

/**
 * A signed-out caller reaches this through the public invitation route. Rejection is not
 * special-cased here: the client's own unauthenticated handling sends the browser to
 * sign-in with this page's own URL as `returnTo`, token included, and the round trip lands
 * back here for another attempt.
 */
export function useAcceptInvitation(): UseAcceptInvitation {
  const api = useApi()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (token: string) => api.invitations.accept(token),
    onSuccess: () => {
      for (const key of invitationKeys.invalidatedByAccept())
        void queryClient.invalidateQueries({ queryKey: key })
    },
  })

  return {
    isAccepting: computed(() => mutation.isPending.value),
    error: computed(() => mutation.error.value),
    membership: computed(() => mutation.data.value),
    accept: (token) => mutation.mutate(token),
  }
}
